import { FC } from "react";
import { OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import { InfoCircle, Truck } from "react-bootstrap-icons";

import { Vessel } from "src/assets/vessel.tsx";
import { ContainerEvent } from "src/model/subscription.ts";
import { buildPortString } from "src/utils/ports.ts";
import { formatDateTime } from "src/utils/timeFormat.ts";

import styles from "./eventList.module.scss";

interface ITransportMode {
  transportMode: string | null;
  vesselName: string | null;
  voyage: string | null;
}

const TransportMode: FC<ITransportMode> = ({ transportMode, voyage, vesselName }) => {
  if (transportMode === null) {
    return null;
  }

  if (transportMode === "truck") {
    return (
      <div>
        <div>
          <Truck className="me-1" /> Truck
        </div>
        {voyage !== null ? <div className="fs-6">Voyage No: {voyage}</div> : null}
      </div>
    );
  }

  if (transportMode === "vessel") {
    return (
      <div>
        <div className="d-flex flex-row align-items-center">
          <Vessel height="1.2rem" width="1.2rem" className="me-1" /> {vesselName ?? "Vessel"}
        </div>
        {voyage !== null ? <div className="fs-6">Voyage No: {voyage}</div> : null}
      </div>
    );
  }
};

interface IProcessIndicator {
  processType: "past" | "actual" | "upcoming";
  isFirst: boolean;
  isLast: boolean;
}

const ProcessIndicator: FC<IProcessIndicator> = ({ processType, isFirst, isLast }) => {
  return (
    <div className="d-flex flex-column align-items-center justify-content-center h-100">
      <div
        className={`flex-grow-1 ${styles.line} ${processType === "upcoming" ? styles.upcoming : styles.past}`}
        style={{ borderWidth: isFirst ? 0 : "1px" }}
      />
      <div className={`${styles.processIndicator} ${processType === "upcoming" ? styles.upcoming : styles.past}`} />
      <div
        className={`flex-grow-1 ${styles.line} ${processType === "past" ? styles.past : styles.upcoming}`}
        style={{ borderWidth: isLast ? 0 : "1px" }}
      />
    </div>
  );
};

interface EventListProps {
  events: ContainerEvent[];
}

export const EventList: FC<EventListProps> = ({ events }) => {
  return (
    <Table className="m-0 align-middle" style={{ height: "fit-content" }}>
      <thead>
        <tr>
          <th style={{ width: "34px" }} />
          <th>Date</th>
          <th>Location</th>
          <th>Milestone</th>
          <th>Transport Mode</th>
        </tr>
      </thead>
      <tbody className="table-group-divider">
        {events.map((event, index) => (
          <tr key={`event-${index}`}>
            <td className={`pt-0 pe-0 pb-0 h-100 position-relative ${index !== events.length - 1 ? "border-0" : ""}`}>
              <ProcessIndicator
                processType={
                  index === events.findLastIndex((e) => e.dateTimeConfirmed)
                    ? "actual"
                    : event.dateTimeConfirmed
                      ? "past"
                      : "upcoming"
                }
                isFirst={index === 0}
                isLast={index === events.length - 1}
              />
            </td>
            <td>{formatDateTime(event.dateTime)}</td>
            <td>{buildPortString(event.unlocodeName, event.unlocode)}</td>
            <td>
              {event.lineReported && event.lineReported.length > 0 ? (
                <OverlayTrigger
                  placement="auto"
                  overlay={<Tooltip style={{ whiteSpace: "pre-line" }}>{event.lineReported}</Tooltip>}
                >
                  <span className="d-inline-flex align-items-center">
                    {event.milestone}&nbsp;&nbsp;
                    <InfoCircle />
                  </span>
                </OverlayTrigger>
              ) : (
                <span className="d-inline-flex align-items-center">{event.milestone}</span>
              )}
            </td>
            <td>
              <TransportMode transportMode={event.transportMode} voyage={event.voyage} vesselName={event.vesselName} />
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};
